import { useTranslations } from "use-intl";
import type {
  FlightType,
  MappedFlightOffer,
} from "../lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { usePaymentContext } from "../lib/context/PaymentContext";
import { extractAirlineCarriers } from "../lib/utils/extractAirlineCarriers";
import { Feature, isFeatureEnabled } from "../lib/utils/featureFlags";
import { SingleAvailableFlight } from "./SingleAvailableFlight";
import { SingleFlightInfo } from "./SingleFlightInfo";
import { formatCurrency } from "../lib/utils/currency";
import clsx from "clsx";
import {
  calculateMonthlyInstallment,
  includeBnplFee,
} from "../lib/utils/bnpl-fee";
import { BaggageIncluded } from "./flight-list/BaggageIncluded";

type Props = {
  flight: FlightType;
  offer: MappedFlightOffer;
  direction: "departure" | "return";
  onClick: () => void;
};

export const SingleAvailableFlightV2 = ({
  flight,
  offer,
  direction,
  onClick,
}: Props) => {
  const t = useTranslations();
  const allAirlines = extractAirlineCarriers(flight.segments);

  const isTwoWaySearch = !!offer.flightsOverview[0].returnFlight;

  const firstSegment = flight.segments[0];
  const lastSegment = flight.segments[flight.segments.length - 1];
  const doesOperatingCarrierDiffer =
    firstSegment.operatingCarrier.airline.code !==
    firstSegment.marketingCarrier.airline.code;

  const { maxAvailableInstallments, isEligible } = usePaymentContext();
  const isBnplFeatureEnabled =
    isFeatureEnabled(Feature.Bnpl) && Boolean(isEligible);
  const includedBnplFee = includeBnplFee(offer.totalPrice);
  const monthlyPayment = calculateMonthlyInstallment(
    includedBnplFee,
    maxAvailableInstallments,
  );

  const isBaggageIncluded = offer.baggageAllowanceList.some((list) =>
    list.baggageAllowance.some((allowance) => Number(allowance.quantity) > 0),
  );

  return (
    <SingleAvailableFlight
      onClick={onClick}
      airlines={allAirlines}
      topInfo={isBaggageIncluded ? <BaggageIncluded /> : null}
      bottomInfo={
        <div className="flex w-full justify-between gap-2">
          {isBnplFeatureEnabled ? (
            <div
              className={clsx("flex grow items-start justify-between", {
                "flex-col": isBnplFeatureEnabled,
              })}
            >
              <div className="text-xs text-text-tertiary">
                {t("payment.payMonthly")}
              </div>
              <span className="font-bold text-text-primary" dir="ltr">
                {direction === "return"
                  ? "+0 IQD"
                  : formatCurrency(monthlyPayment, offer.currency)}
              </span>
            </div>
          ) : null}
          <div
            className={clsx("flex grow items-end justify-between", {
              "flex-col": isBnplFeatureEnabled,
            })}
          >
            <div className="text-xs text-text-tertiary">
              {isTwoWaySearch
                ? t("payment.totalRoundTripPrice")
                : t("payment.standard")}
            </div>
            <span className="font-bold text-text-primary" dir="ltr">
              {direction === "return"
                ? "+0 IQD"
                : formatCurrency(offer.totalPrice, offer.currency)}
            </span>
          </div>
        </div>
      }
    >
      <SingleFlightInfo
        origin={{
          date: firstSegment.departure.date,
          time: firstSegment.departure.time,
          code: firstSegment.departure.airport.code,
        }}
        destination={{
          date: lastSegment.arrival.date,
          time: lastSegment.arrival.time,
          code: lastSegment.arrival.airport.code,
        }}
        duration={flight.duration}
        stops={flight.stops?.length || 0}
        operatedBy={
          doesOperatingCarrierDiffer
            ? firstSegment.operatingCarrier.airline.name ||
              firstSegment.operatingCarrier.airline.code
            : undefined
        }
      />
    </SingleAvailableFlight>
  );
};
